<template>
	<div class="notification-timeline-item" @mouseover="hover = true" @mouseleave="hover = false">
		<div class="notification-card" :class="{ 'notification-card--unread': !item.isViewed, 'notification-card--hover': hover }"
			@click="onClickItem(item)">
			<div class="notification-select">
				<b-form-checkbox v-if="selectable" :checked="selected"
					@change="$emit('toggle-selected', item.id, $event)" @click.stop />
			</div>
			<div class="notification-icon-wrap" :class="priorityClass">
				<img :src="iconRenderer(item.type)" alt class="notification-icon" />
			</div>
			<div class="notification-content">
				<div class="notification-header">
					<h6 class="notification-title">{{ item.title }}</h6>
					<span class="priority-pill" :class="priorityClass">{{ priorityLabel }}</span>
				</div>
				<div v-if="!discrepancy && !resolvedDispatch" class="notification-body">
					{{ item.content }}
				</div>
				<div v-if="discrepancy" class="notification-body">
					{{ discrepancy.content }}
				</div>
				<div v-if="resolvedDispatch" class="notification-body">
					{{ resolvedDispatch.content }}
				</div>
				<div class="notification-meta-row">
					<span v-if="locationLabel" class="notification-location">
						<i class="fa fa-map-marker"></i>
						{{ locationLabel }}
					</span>
					<div class="notification-time">
						<time-ago :refresh="60" :datetime="dateSent" locale="en" :tooltip="true" long />
					</div>
				</div>
				<div v-if="hasAction" class="notification-footer">
					<div class="notification-actions">
						<b-link v-if="discrepancy" @click="redirectDiscrepancyReport(discrepancy)" class="primary-action">
							Review discrepancy <span aria-hidden="true">&rarr;</span>
						</b-link>
						<b-link v-if="resolvedDispatch" @click="redirectDiscrepancyReport(resolvedDispatch)" class="primary-action">
							View resolved report <span aria-hidden="true">&rarr;</span>
						</b-link>
						<b-link v-if="dispatchId" @click="openDispatchDetails(item)" class="primary-action">
							See details of {{ dispatchId }} <span aria-hidden="true">&rarr;</span>
						</b-link>
						<b-link v-if="incidentId" @click="openIncidentDetails(item)" class="primary-action">
							See details of {{ incidentId }} <span aria-hidden="true">&rarr;</span>
						</b-link>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// Component
import dispatch from '@/assets/images/notifications/dispatch.svg';
import inTransit from '@/assets/images/notifications/in-transit.svg';
import receive from '@/assets/images/notifications/receive.svg';
import welcome from '@/assets/images/notifications/welcome.svg';
import locked from '@/assets/images/notifications/locked.svg';
import changeLocation from '@/assets/images/notifications/change-location.svg';
import incidentReport from '@/assets/images/notifications/incident-report.svg';
import approve from '@/assets/images/notifications/approve.svg';
import warning from '@/assets/images/notifications/warning.svg';
import warningHexagon from '@/assets/images/notifications/warning-hexagon.svg';
import meterPlus from '@/assets/images/notifications/meter-plus.svg';
import meterMinus from '@/assets/images/notifications/meter-minus.svg';

// DAO
import dispatchDAO from '@/database/dispatches';
import incidentReportDAO from '@/database/incidentReports';
import notificationDAO from '@/database/notifications';

// Util
import { DateUtil } from '@/utils/dateutil';

// Others
import config from '@/config/env-constants';
import EventBus from '@/shared/event-bus';
import TimeAgo from 'vue2-timeago';


export default {
	name: 'NotificationItem',
	components: { TimeAgo },
	data() {
		return {
			hover: false,
			icons: {
				dispatch,
				inTransit,
				receive,
				welcome,
				locked,
				changeLocation,
				incidentReport,
                approve,
                warning,
                warningHexagon,
                meterPlus,
                meterMinus
			},
		};
	},
	props: {
		item: {
			type: Object,
			required: true,
		},
		selectable: {
			type: Boolean,
			default: false,
		},
		selected: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		dateSent() {
			return DateUtil.getFormattedDateWithTime(this.item.dateSent);
		},
		dispatchId() {
			const item = this.item ? this.item : {};
			return item.meta && item.meta.dispatchId ? item.meta.dispatchId : null;
		},
		incidentId() {
			const item = this.item ? this.item : {};
			return item.meta && item.meta.incidentId ? item.meta.incidentId : null;
		},
        discrepancy() {
            const item = this.item ? this.item : {};
            return item.type && item.type === config.notificationType.DISCREPANCY ? { ...item } : null;
        },
		resolvedDispatch() {
            const item = this.item ? this.item : {};
            return item.type && item.type === config.notificationType.RESOLVED_DISCREPANCY_REPORT ? { ...item } : null;
        },
		hasAction() {
			return this.discrepancy || this.resolvedDispatch || this.dispatchId || this.incidentId;
		},
		locationLabel() {
			const item = this.item || {};
			const meta = item.meta || {};
			const location = meta.locationName || meta.location || meta.branchName || meta.siteName || meta.companyName;

			if (location) {
				return location;
			}

			const content = this.discrepancy ? this.discrepancy.content : item.content;
			const match = content ? content.match(/\[([^\]]+)\]/) : null;
			return match ? match[1] : '';
		},
		priorityLabel() {
			const explicitPriority = this.item.priority || this.item.severity || (this.item.meta && this.item.meta.priority);

			if (explicitPriority) {
				return String(explicitPriority).toUpperCase();
			}

			if (
				this.item.type === config.notificationType.DISCREPANCY ||
				this.item.type === config.notificationType.CONTRACT_LIMIT_EXCEEDED ||
				this.item.type === config.notificationType.MAX_LIMIT_EXCEEDED ||
				this.item.type === config.notificationType.MIN_LIMIT_EXCEEDED
			) {
				return 'Critical';
			}

			if (this.item.type === config.notificationType.RESOLVED_DISCREPANCY_REPORT) {
				return 'Resolved';
			}

			return 'Info';
		},
		priorityClass() {
			const label = this.priorityLabel.toLowerCase();

			if (label === 'critical') {
				return 'priority-critical';
			}

			if (label === 'high') {
				return 'priority-high';
			}

			if (label === 'medium') {
				return 'priority-medium';
			}

			if (
				this.item.type === config.notificationType.DISCREPANCY ||
				this.item.type === config.notificationType.CONTRACT_LIMIT_EXCEEDED ||
				this.item.type === config.notificationType.MAX_LIMIT_EXCEEDED ||
				this.item.type === config.notificationType.MIN_LIMIT_EXCEEDED
			) {
				return 'priority-high';
			}

			if (this.item.type === config.notificationType.RESOLVED_DISCREPANCY_REPORT) {
				return 'priority-success';
			}

			return 'priority-normal';
		},
	},
	methods: {
		async onClickItem(item) {
			try {
				if (item && !item.isViewed) {
					await notificationDAO.setAsViewed(item.id);
					this.$emit('viewed', item.id);
				}
			} catch (error) {
				this.$toaster.error('Error loading data. Please reload the page again.');
			}
		},
		iconRenderer(type) {
			let icon;
			if (type === config.notificationType.NEW_COMPANY) {
				icon = this.icons.welcome;
			} else if (type === config.notificationType.DISPATCH_DEPLOYED) {
				icon = this.icons.dispatch;
			} else if (type === config.notificationType.DISPATCH_IN_TRANSIT) {
				icon = this.icons.inTransit;
			} else if (type === config.notificationType.DISPATCH_RECEIVED) {
				icon = this.icons.receive;
			} else if (type === config.notificationType.LOCKED_ACCOUNT) {
				icon = this.icons.locked;
			} else if (type === config.notificationType.UNLOCKED_ACCOUNT) {
				icon = this.icons.welcome;
			} else if (type === config.notificationType.CHANGE_LOCATION) {
				icon = this.icons.changeLocation;
			} else if (type === config.notificationType.INCIDENT_REPORT) {
				icon = this.icons.incidentReport;
            } else if (type === config.notificationType.DISCREPANCY) {
                icon = this.icons.warning;
            } else if (type === config.notificationType.RESOLVED_DISCREPANCY_REPORT) {
                icon = this.icons.approve;
            } else if (type === config.notificationType.CONTRACT_LIMIT_EXCEEDED) {
                icon = this.icons.warningHexagon;
            } else if (type === config.notificationType.MAX_LIMIT_EXCEEDED) {
                icon = this.icons.meterPlus;
            } else if (type === config.notificationType.MIN_LIMIT_EXCEEDED) {
                icon = this.icons.meterMinus;
			} else {
				// Default mail icon
				icon = 'https://images.clipartlogo.com/files/istock/previews/7702/77021021-email-colored-vector-icon.jpg';
			}

			return icon;
		},
		async openDispatchDetails(notification) {
			const dispatchId = notification.meta ? notification.meta.dispatchId : null;

			if (dispatchId) {
				let dispatchObj = await dispatchDAO.getDispatchByDispatchId(dispatchId);
				EventBus.$emit('onShowDispatchDetails', dispatchObj);
			}
		},

		async openIncidentDetails(notification) {
			const incidentId = notification.meta ? notification.meta.incidentId : null;

			if (incidentId) {
				let incidentReportObj = await incidentReportDAO.getIncidentReportByIncidentId(incidentId);
				EventBus.$emit('onShowIncidentReportDetails', incidentReportObj);
			}
		},
        redirectDiscrepancyReport(item) {
            const { type, meta: { dispatchNo, dateDeployed } } = item;

            const status = type === config.notificationType.DISCREPANCY ? 'Pending' : 'Resolved';

            const url = this.$store.getters.isSuperAdmin
                ? "/admin/admin-discrepancy-reports"
                : "/discrepancy-reports";

            this.$router.replace(`${url}/${dispatchNo}?dateDeployed=${dateDeployed}&status=${status}`);
        },
	},
};
</script>

<style scoped>
.notification-timeline-item {
	display: flex;
	position: relative;
}

.notification-card {
	background: #ffffff;
	border: 1px solid #dce7f2;
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(18, 44, 72, 0.06);
	cursor: pointer;
	display: grid;
	grid-template-columns: 24px 54px 1fr;
	margin: 0 0 14px;
	padding: 14px 14px 0 10px;
	transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
	width: 100%;
}

.notification-card--hover {
	border-color: #b8cce4;
	box-shadow: 0 8px 22px rgba(18, 44, 72, 0.12);
	transform: translateY(-1px);
}

.notification-card--unread {
	background: #ffffff;
}

.notification-select {
	padding-top: 2px;
}

.notification-icon-wrap {
	align-items: center;
	border: 1px solid #f1dfc7;
	border-radius: 7px;
	display: flex;
	height: 48px;
	justify-content: center;
	margin-top: 0;
	width: 48px;
}

.notification-icon {
	height: 31px;
	object-fit: contain;
	width: 31px;
}

.notification-content {
	min-width: 0;
}

.notification-header {
	align-items: flex-start;
	display: flex;
	gap: 12px;
	justify-content: space-between;
	margin-bottom: 7px;
	width: 100%;
}

.notification-title {
	color: #102033;
	font-size: 15px;
	font-weight: 700;
	line-height: 1.25;
	margin: 0;
	min-width: 0;
}

.priority-pill {
	border-radius: 6px;
	display: inline-block;
	flex: 0 0 auto;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0;
	line-height: 1;
	margin-top: 0;
	padding: 6px 8px;
	text-transform: uppercase;
}

.notification-body {
	color: #24364a;
	font-size: 13px;
	line-height: 1.5;
	margin-bottom: 10px;
}

.notification-meta-row {
	align-items: center;
	display: flex;
	gap: 8px;
	justify-content: space-between;
	margin-bottom: 12px;
}

.notification-location {
	color: #617184;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.01em;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	text-transform: uppercase;
	white-space: nowrap;
}

.notification-location .fa {
	color: #687b8f;
	margin-right: 5px;
}

.notification-footer {
	border-top: 1px solid #edf1f5;
	margin-left: -86px;
	padding: 11px 0 12px;
	width: calc(100% + 86px);
}

.notification-actions {
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 6px;
	width: 100%;
}

.primary-action {
	background: transparent;
	border: 0;
	border-radius: 0;
	color: #1681ff;
	font-size: 13px;
	font-weight: 700;
	line-height: 1.2;
	padding: 0;
}

.primary-action:hover {
	background: transparent;
	color: #006fda;
	text-decoration: none;
}

.notification-time {
	color: #34465a;
	flex: 0 0 auto;
	font-size: 12px;
	text-align: right;
}

.priority-critical.notification-icon-wrap,
.priority-high.notification-icon-wrap {
	background: #fff4da;
}

.priority-critical.priority-pill {
	background: #fff0d8;
	color: #ff7a00;
}

.priority-high.priority-pill {
	background: #fff4da;
	color: #986100;
}

.priority-medium.notification-icon-wrap {
	background: #fff4da;
}

.priority-medium.priority-pill {
	background: #fff4da;
	color: #ffae00;
}

.priority-success.notification-icon-wrap {
	background: #e4f6ec;
}

.priority-success.priority-pill {
	background: #e4f6ec;
	color: #137a3d;
}

.priority-normal.notification-icon-wrap {
	background: #eaf2fb;
}

.priority-normal.priority-pill {
	background: #eaf2fb;
	color: #024f98;
}

@media (max-width: 575.98px) {
	.notification-card {
		grid-template-columns: 24px 44px 1fr;
		padding-right: 10px;
	}

	.notification-header,
	.notification-meta-row {
		align-items: flex-start;
		flex-direction: column;
	}

	.notification-footer {
		margin-left: -78px;
		padding-left: 0;
		width: calc(100% + 78px);
	}

	.notification-time {
		text-align: left;
	}
}
</style>
